import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { animateOrSkip, SPRING } from '@theme/motion';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
	requiredRefs = ['dialog'];

	connectedCallback() {
		super.connectedCallback();

		if (this.minWidth || this.maxWidth) {
			window.addEventListener('resize', this.#handleResize);
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.minWidth || this.maxWidth) {
			window.removeEventListener('resize', this.#handleResize);
		}
	}

	#handleResize = debounce(() => {
		const { minWidth, maxWidth } = this;

		if (!minWidth && !maxWidth) return;

		const windowWidth = window.innerWidth;
		if (windowWidth < minWidth || windowWidth > maxWidth) {
			this.closeDialog();
		}
	}, 50);

	#previousScrollY = 0;

	/**
	 * Shows the dialog.
	 */
	showDialog() {
		const { dialog } = this.refs;

		if (dialog.open) return;

		const scrollY = window.scrollY;
		this.#previousScrollY = scrollY;

		// Measure the scrollbar width (and any existing body padding) up-front, so we
		// can compensate when `position: fixed` removes the page scrollbar — otherwise
		// the page shifts sideways as the scrollbar disappears on open and returns on
		// close.
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
		const basePaddingRight = parseFloat(getComputedStyle(document.body).paddingRight) || 0;

		// Prevent layout thrashing by separating DOM reads from DOM writes
		requestAnimationFrame(() => {
			document.body.style.width = '100%';
			document.body.style.position = 'fixed';
			document.body.style.top = `-${scrollY}px`;
			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${basePaddingRight + scrollbarWidth}px`;
			}

			dialog.showModal();
			this.dispatchEvent(new DialogOpenEvent());

			this.addEventListener('click', this.#handleClick);
			this.addEventListener('keydown', this.#handleKeyDown);

			this.#enhanceOpen(dialog);
		});
	}

	/**
	 * Closes the dialog.
	 */
	closeDialog = async () => {
		const { dialog } = this.refs;

		if (!dialog.open) return;

		this.removeEventListener('click', this.#handleClick);
		this.removeEventListener('keydown', this.#handleKeyDown);

		await this.#animateClose(dialog);

		document.body.style.width = '';
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.paddingRight = '';
		window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

		dialog.close();
		dialog.classList.remove('dialog-closing');
		dialog.removeAttribute('data-motion');
		dialog.style.transform = '';
		dialog.style.opacity = '';

		this.dispatchEvent(new DialogCloseEvent());
	};

	/**
	 * Toggles the dialog.
	 */
	toggleDialog = () => {
		if (this.refs.dialog.open) {
			this.closeDialog();
		} else {
			this.showDialog();
		}
	};

	/**
	 * Enhances the drawer open with a Motion spring slide-in (progressive
	 * enhancement). Only applies to `.dialog-drawer` dialogs; modals keep their
	 * existing CSS animation. Falls back to the CSS keyframe when Motion is
	 * unavailable or the user prefers reduced motion.
	 *
	 * @param {HTMLDialogElement} dialog - The dialog element.
	 */
	#enhanceOpen(dialog) {
		if (prefersReducedMotion()) return;
		if (!dialog.classList.contains('dialog-drawer')) return;

		const { startX, startY, startOpacity, endOpacity } = this.#readDrawerMotion(dialog);
		const restX = this.#restValue(startX);
		const restY = this.#restValue(startY);

		// Suppress the CSS open keyframe and hold the panel at its off-screen
		// start position so there is no flash while Motion lazy-loads.
		dialog.setAttribute('data-motion', '');
		dialog.style.transform = `translate(${startX}, ${startY})`;
		dialog.style.opacity = String(startOpacity);

		animateOrSkip(
			dialog,
			{ x: [startX, restX], y: [startY, restY], opacity: [startOpacity, endOpacity] },
			SPRING.sheet
		).then((ran) => {
			// Drop the suppression if Motion never ran so the drawer simply rests open.
			if (!ran) dialog.removeAttribute('data-motion');
			dialog.style.transform = '';
			dialog.style.opacity = '';
		});
	}

	/**
	 * Plays the drawer close animation. Uses a Motion spring when the open was
	 * enhanced (`data-motion`); otherwise falls back to the original CSS
	 * `dialog-closing` flow.
	 *
	 * @param {HTMLDialogElement} dialog - The dialog element.
	 */
	async #animateClose(dialog) {
		if (dialog.hasAttribute('data-motion')) {
			// Read the motion props first — before `dialog-closing` rewrites the
			// `--start-*` custom properties below.
			const { startX, startY, startOpacity, endOpacity } = this.#readDrawerMotion(dialog);
			const restX = this.#restValue(startX);
			const restY = this.#restValue(startY);

			// Add `dialog-closing` so the ::backdrop fades out via CSS in sync with
			// the panel — without it the dimming overlay pops off at the end, which
			// reads as the page "snapping" back. The panel keyframe stays suppressed
			// by `[data-motion]`, so only Motion moves the panel.
			dialog.classList.add('dialog-closing');

			const ran = await animateOrSkip(
				dialog,
				{ x: [restX, startX], y: [restY, startY], opacity: [endOpacity, startOpacity] },
				SPRING.sheetClose
			);

			if (ran) return;

			// Motion unavailable: drop the enhancement so the CSS closing keyframe runs.
			dialog.removeAttribute('data-motion');
		}

		// CSS fallback (original behaviour): restart and play the closing keyframe.
		dialog.style.animation = 'none';
		void dialog.offsetWidth;
		dialog.classList.add('dialog-closing');
		dialog.style.animation = '';

		await onAnimationEnd(dialog, undefined, { subtree: false });
	}

	/**
	 * Reads the drawer's animation custom properties — the same ones that drive
	 * the `move-and-fade` keyframe — so Motion animates in whatever direction the
	 * drawer is configured for (right/left side drawers and bottom sheets alike).
	 *
	 * @param {HTMLDialogElement} dialog - The dialog element.
	 */
	#readDrawerMotion(dialog) {
		const styles = getComputedStyle(dialog);
		const read = (name, fallback) => {
			const value = styles.getPropertyValue(name).trim();
			return value === '' ? fallback : value;
		};

		return {
			startX: read('--start-x', '0px'),
			startY: read('--start-y', '0px'),
			startOpacity: Number(read('--start-opacity', '1')),
			endOpacity: Number(read('--end-opacity', '1')),
		};
	}

	/**
	 * Returns a zero value in the same unit as `value` (e.g. `"100%"` -> `"0%"`),
	 * so Motion interpolates without mixing units.
	 *
	 * @param {string} value - A CSS length/percentage such as `"100%"` or `"-100%"`.
	 * @returns {string} The matching zero value, e.g. `"0%"` or `"0px"`.
	 */
	#restValue(value) {
		const unit = String(value).match(/[a-z%]+$/i)?.[0] ?? 'px';
		return `0${unit}`;
	}

	/**
	 * Closes the dialog when the user clicks outside of it.
	 *
	 * @param {MouseEvent} event - The mouse event.
	 */
	#handleClick(event) {
		const { dialog } = this.refs;

		if (isClickedOutside(event, dialog)) {
			this.closeDialog();
		}
	}

	/**
	 * Closes the dialog when the user presses the escape key.
	 *
	 * @param {KeyboardEvent} event - The keyboard event.
	 */
	#handleKeyDown(event) {
		if (event.key !== 'Escape') return;

		event.preventDefault();
		this.closeDialog();
	}

	/**
	 * Gets the minimum width of the dialog.
	 *
	 * @returns {number} The minimum width of the dialog.
	 */
	get minWidth() {
		return Number(this.getAttribute('dialog-active-min-width'));
	}

	/**
	 * Gets the maximum width of the dialog.
	 *
	 * @returns {number} The maximum width of the dialog.
	 */
	get maxWidth() {
		return Number(this.getAttribute('dialog-active-max-width'));
	}
}

if (!customElements.get('dialog-component'))
	customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
	constructor() {
		super(DialogOpenEvent.eventName);
	}

	static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
	constructor() {
		super(DialogCloseEvent.eventName);
	}

	static eventName = 'dialog:close';
}

document.addEventListener(
	'toggle',
	(event) => {
		if (event.target instanceof HTMLDetailsElement) {
			if (event.target.hasAttribute('scroll-lock')) {
				const { open } = event.target;

				if (open) {
					document.documentElement.setAttribute('scroll-lock', '');
				} else {
					document.documentElement.removeAttribute('scroll-lock');
				}
			}
		}
	},
	{ capture: true }
);
